import { bus } from '../core/event-bus.js';
import { playerManager } from '../core/player-manager.js';
import { store } from '../core/state-store.js';
import { formatDecimalDisplay, formatValue } from '../utils/format.js';
import { el } from '../utils/dom.js';

const GROUPS = [
  {
    label: 'Playback',
    keys: [
      'adOnStage', 'adPaused', 'adCurrentTime', 'adRemainingTime',
      'adDuration', 'adLinear', 'contentPlayerCompleted',
    ],
  },
  {
    label: 'Audio',
    keys: ['volume', 'muted', 'audioSignalDetected', 'audioSignalLevel'],
  },
  {
    label: 'Media',
    keys: ['adMediaUrl', 'adContentType', 'adMediaWidth', 'adMediaHeight'],
  },
  {
    label: 'Ad Metadata',
    keys: [
      'adTagUrl', 'adTitle', 'adDescription', 'adSystem', 'adUniversalAdIds',
      'adAdvertiser', 'adPricing', 'adSurvey', 'adAdServingId',
      'adCategories', 'adBlockedAdCategories',
    ],
  },
  {
    label: 'Skippable / Interaction',
    keys: ['isSkippableAd', 'adSkippableState', 'skipTimeOffset', 'clickThroughUrl'],
  },
  {
    label: 'Runtime / Errors',
    keys: ['initialized', 'adErrorMessage', 'adVastErrorCode'],
  },
];

const GETTERS = GROUPS.flatMap((group) => group.keys);

let prevValues = {};
let rows = {};
const TIMING_DISPLAY_KEYS = new Set(['adDuration', 'adCurrentTime', 'adRemainingTime']);
const DECIMAL_DISPLAY_KEYS = new Set(['audioSignalLevel']);
const PERCENT_DISPLAY_KEYS = new Set(['volume']);

export function initApiInspector() {
  const table = document.getElementById('api-table');
  const stateBadge = document.getElementById('api-inspector-state-badge');

  for (const group of GROUPS) {
    const section = el('section', {
      className: 'api-section',
      'data-testid': `api-section-${group.label.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-')}`,
    });

    const title = el('h4', {
      className: 'api-section-title',
      textContent: group.label,
    });
    section.appendChild(title);

    for (const key of group.keys) {
      const valueSpan = el('span', {
        className: 'api-value',
        'data-testid': `api-value-${key}`,
        textContent: '—',
      });

      const row = el('div', {
        className: 'api-row',
        'data-testid': `api-row-${key}`,
      }, [
        el('span', { className: 'api-key', textContent: key }),
        valueSpan,
      ]);

      section.appendChild(row);
      rows[key] = { row, valueSpan };
    }

    table.appendChild(section);
  }

  // Update on tick and events
  bus.on('player:tick', () => _refresh(stateBadge));
  bus.on('ad:event', () => _refresh(stateBadge));
  bus.on('player:initialized', () => _refresh(stateBadge));
  bus.on('state:changed', () => _refresh(stateBadge));

  _refresh(stateBadge);
}

function _refresh(stateBadge) {
  const state = store.state;
  const frozenSnapshot = store.finalSnapshot;
  const values = state.sessionStatus === 'running'
    ? playerManager.readAllGetters()
    : (frozenSnapshot || {});

  _syncStateBadge(stateBadge, state, frozenSnapshot);

  for (const key of GETTERS) {
    const val = values[key];
    const formatted = _formatInspectorValue(key, val, {
      showPlaceholder: state.sessionStatus === 'idle' && !frozenSnapshot,
    });
    const entry = rows[key];
    if (!entry) continue;

    const prev = prevValues[key];
    entry.valueSpan.textContent = formatted;

    if (prev !== undefined && prev !== formatted) {
      entry.row.classList.add('changed');
      setTimeout(() => entry.row.classList.remove('changed'), 600);
    }

    prevValues[key] = formatted;
  }
}

function _syncStateBadge(stateBadge, state, frozenSnapshot) {
  if (!stateBadge) return;

  const isFrozenTerminalState = Boolean(frozenSnapshot) && state.sessionStatus !== 'running' && state.sessionStatus !== 'idle';
  const status = String(state.sessionStatus || 'complete').toLowerCase();

  if (!isFrozenTerminalState) {
    stateBadge.textContent = '';
    stateBadge.classList.remove('panel-state-badge-complete', 'panel-state-badge-error');
    stateBadge.classList.add('hidden');
    return;
  }

  stateBadge.textContent = `END STATE: ${status.toUpperCase()}`;
  stateBadge.classList.toggle('panel-state-badge-complete', status === 'complete');
  stateBadge.classList.toggle('panel-state-badge-error', status === 'error');
  stateBadge.classList.remove('hidden');
}

function _formatInspectorValue(key, val, { showPlaceholder = false } = {}) {
  if (showPlaceholder && val === undefined) {
    return '—';
  }

  if (TIMING_DISPLAY_KEYS.has(key)) {
    const formattedTiming = formatDecimalDisplay(val, 2);
    if (formattedTiming !== null) {
      return formattedTiming;
    }
  }

  if (DECIMAL_DISPLAY_KEYS.has(key)) {
    const formattedDecimal = formatDecimalDisplay(val, 4);
    if (formattedDecimal !== null) {
      return formattedDecimal;
    }
  }

  if (PERCENT_DISPLAY_KEYS.has(key)) {
    const parsed = typeof val === 'number' ? val : Number(val);
    if (Number.isFinite(parsed)) {
      const pct = Math.max(0, Math.min(100, Math.round(parsed * 100)));
      return `${pct}%`;
    }
  }

  return formatValue(val);
}
